import { useCallback, useMemo } from "react";
import { debug as debugFn } from "debug";
import { errAsync, ResultAsync } from "neverthrow";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import type {
  IncidentWorkItemWithContext,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { useUser } from "../../Context/UserContext.js";
import type { DetentionDetailsFormProps } from "../../forms/DetentionDetails/DetentionDetailsForm.js";
import {
  DetentionDetailsForm,
  DetentionDetailsSkeleton,
} from "../../forms/DetentionDetails/DetentionDetailsForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:PersonDetailsPage");

export type DetentionDetailsPageProps = Record<string, unknown>;

// This component is responsible for managing the API calls and passing
// the data to the inner component.
export function DetentionDetailsPage(_props: DetentionDetailsPageProps) {
  const params = useParams();

  // Get the user
  const useUserResponse = useUser();
  if (useUserResponse.loading) {
    return <DetentionDetailsPageInnerLoading />;
  }
  if (
    useUserResponse.error ||
    useUserResponse.user === undefined ||
    useUserResponse.user.sessionContext === null
  ) {
    return <ErrorComponent />;
  }

  // Get the incident work item
  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [getIncidentWorkItemResponse, refetchWorkItem] =
    apiHooks.incidentWorkItem.get(incidentWorkItemId.data);
  if (getIncidentWorkItemResponse.loading) {
    return <DetentionDetailsPageInnerLoading />;
  }
  if (getIncidentWorkItemResponse.error) {
    return <ErrorComponent />;
  }

  return (
    <DetentionDetailsPageInner
      incidentWorkItem={getIncidentWorkItemResponse.data ?? undefined}
      refetchWorkItem={refetchWorkItem}
    />
  );
}

export type DetentionDetailsPageInnerProps = {
  incidentWorkItem?: IncidentWorkItemWithContext;
  refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
};

export function DetentionDetailsPageInner({
  incidentWorkItem,
  refetchWorkItem,
}: DetentionDetailsPageInnerProps) {
  if (incidentWorkItem === undefined) {
    return <DetentionDetailsPageInnerLoading />;
  }

  const hasSection136Form = incidentWorkItem.forms.some(
    (form) => form.formType === IncidentFormTypeSchema.enum.section136,
  );
  if (!hasSection136Form) {
    return (
      <Navigate
        replace
        to={createRoute.incidentProgressPage(
          incidentWorkItem.incidentWorkItem.id,
        )}
      />
    );
  }

  return (
    <DetentionDetailsPageInnerLoaded
      incidentWorkItem={incidentWorkItem}
      refetchWorkItem={refetchWorkItem}
    />
  );
}

function DetentionDetailsPageInnerLoading() {
  return <DetentionDetailsSkeleton />;
}

function DetentionDetailsPageInnerLoaded({
  incidentWorkItem,
  refetchWorkItem,
}: Required<DetentionDetailsPageInnerProps>) {
  const navigate = useNavigate();
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();

  // Get incident information
  const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;

  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  // Get 136 information
  const section136Form = useMemo(
    () =>
      incidentWorkItem.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.section136,
      ),
    [incidentWorkItem.forms],
  );
  if (!section136Form) {
    throw new Error("Section 136 form not found");
  }

  const section136FormId = section136Form.id;

  // Get the form specification for the detention details section (including pre-computed defaultValues)
  const formSpecification = useMemo(() => {
    const existingData =
      section136Form.sections.find(
        (section) => section.id === FormSectionSchema.enum.detentionDetails,
      )?.data ?? {};
    return workflows[workflowVersion]?.[136].formDefinition.sections
      .find((section) => section.id === FormSectionSchema.enum.detentionDetails)
      ?.buildFormSpecification({ incidentWorkItem, existingData });
  }, [workflowVersion, incidentWorkItem, section136Form.sections]);

  if (!formSpecification) {
    throw new Error("Form specification not found");
  }

  const { formSchema: schema, defaultValues: initialValues } =
    formSpecification;

  // Calculate whether the original existing values were valid (before fallbacks)
  const existingValuesWereValid = useMemo(() => {
    const existingData =
      section136Form.sections.find(
        (section) => section.id === FormSectionSchema.enum.detentionDetails,
      )?.data ?? {};
    return schema.safeParse(existingData).success;
  }, [schema, section136Form.sections]);

  // Should we enable auto-save?
  const enableAutoSave = useMemo(
    () =>
      workflows[workflowVersion]?.[136].formDefinition.sections.find(
        (section) => section.id === FormSectionSchema.enum.detentionDetails,
      )?.autoSave ?? false,
    [workflowVersion],
  );

  const onAutoSave: Exclude<
    DetentionDetailsFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      if (existingValuesWereValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = schema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }
      return ResultAsync.fromPromise(
        api.incidentWorkItem.updateData(
          incidentWorkItemId,
          section136FormId,
          FormSectionSchema.enum.detentionDetails,
          values,
          existingValuesWereValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true);
    },
    [incidentWorkItemId, existingValuesWereValid, schema, section136FormId],
  );

  const onSubmit: DetentionDetailsFormProps<
    typeof workflowVersion
  >["onSubmit"] = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        const response = await api.incidentWorkItem.updateData(
          incidentWorkItemId,
          section136FormId,
          FormSectionSchema.enum.detentionDetails,
          values,
          true,
        );
        if (response.status === 204) {
          await refetchWorkItem();
          navigate(`/incident/${incidentWorkItemId}/section-complete`, {
            state: {
              type: FormSectionSchema.enum.detentionDetails,
            },
          });
          return;
        } else {
          throw new Error(JSON.stringify(response.data.message));
        }
      } catch (error) {
        debug("Failed to update Detention Details section", error);
        toastNotifications.show(
          t("incidentWorkItem.notificationFailedToSave"),
          {
            severity: "error",
          },
        );
      }
    },
    [
      incidentWorkItemId,
      navigate,
      refetchWorkItem,
      section136FormId,
      t,
      toastNotifications,
    ],
  );

  const onCancel: DetentionDetailsFormProps<
    typeof workflowVersion
  >["onCancel"] = useCallback(() => {
    navigate(createRoute.incidentProgressPage(incidentWorkItemId));
  }, [incidentWorkItemId, navigate]);

  return (
    <DetentionDetailsForm
      onAutoSave={enableAutoSave ? onAutoSave : undefined}
      onSubmit={onSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      incidentWorkItem={incidentWorkItem}
      workflowVersion={workflowVersion}
    />
  );
}
